import { React, useEffect, useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/projectChange.css';

const AlterarProjeto = () => {
    const [nfs, setNfs] = useState('');
    const [projetosList, setProjetosList] = useState([]);
    const [selectedProjetoFinal, setSelectedProjetoFinal] = useState(null);
    const [errorProjetoFinal, setErrorProjetoFinal] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    useEffect(() => {
        async function getProjetoData() {
            const response = await fetchFlask('getProjeto');
            if (response.status === 'error') {
                handleAlerta(response);
                return;
            }

            const projetos = response.dados
                .map((item) => ({ CodigoProjeto: item.Codigo, NomeProjeto: item.Nome, Departamento: item.Departamento }));
            setProjetosList(projetos);
        }
        getProjetoData();
    }, []);

    async function submitAlterarProjeto (e) {
        e.preventDefault();

        if (!selectedProjetoFinal) {
            setErrorProjetoFinal(!selectedProjetoFinal);
            return;
        }

        setErrorProjetoFinal(false);
        setIsSubmit(true);

        const body = JSON.stringify({
            nfs,
            projeto: selectedProjetoFinal.CodigoProjeto,
            departamento: selectedProjetoFinal.Departamento
        });

        try {
            const response = await fetchFlask('updateProject', body);
            handleAlerta(response);
            if (response.status === 'success') {
                setNfs('');
                setSelectedProjetoFinal(null);
                setErrorProjetoFinal(false);
            }
        } finally {
            setIsSubmit(false);
        }
    };

    const nfsList = nfs
        .split(/[\s,;]+/)
        .map((nf) => nf.trim())
        .filter(Boolean);
    const completedFields = [
        nfsList.length > 0,
        selectedProjetoFinal
    ].filter(Boolean).length;

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    return (
        <Box className='mainBox project-change-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Alterar Projeto
            </PageHeader>

            <form action='' method='POST' id='formAlterarProjeto' onSubmit={submitAlterarProjeto} className='project-change-layout'>
                <Box className='project-change-workspace'>
                    <Box className='project-change-intro'>
                        <Box>
                            <h2>Ajuste de Projeto</h2>
                        </Box>
                        <p>
                            Cole a lista de NFs e selecione o projeto Omie de destino.
                        </p>
                    </Box>

                    <Box className='project-change-panel'>
                        <Box className='project-change-section-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Notas fiscais</strong>
                            </Box>
                        </Box>

                        <Box className='project-change-input-strip'>
                            <ReceiptLongRoundedIcon />
                            <TextField
                                label='NFs'
                                id='nfs'
                                required
                                multiline
                                minRows={7}
                                maxRows={14}
                                value={nfs}
                                onChange={(e) => setNfs(e.target.value)}
                                placeholder='Uma NF por linha, ou separadas por vírgula'
                            />
                        </Box>
                    </Box>

                    <Box className='project-change-panel'>
                        <Box className='project-change-section-heading'>
                            <AccountTreeRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Projeto ERP</strong>
                            </Box>
                        </Box>

                        <Box className='project-change-project-strip'>
                            <SyncAltRoundedIcon />
                            <Box className='project-change-project-fields'>
                                <Autocomplete
                                    id='projetoFinal'
                                    options={projetosList}
                                    value={selectedProjetoFinal}
                                    onChange={(_, value) => {
                                        setSelectedProjetoFinal(value);
                                        setErrorProjetoFinal(false);
                                    }}
                                    getOptionLabel={(option) => option.NomeProjeto}
                                    isOptionEqualToValue={(option, value) => option.CodigoProjeto === value.CodigoProjeto}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='Projeto final'
                                            required
                                            error={errorProjetoFinal}
                                            helperText={errorProjetoFinal ? 'Por favor selecione o projeto final.' : 'Novo projeto das notas fiscais.'}
                                        />
                                    )}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box className='project-change-impact'>
                        <EditRoundedIcon />
                        <Box>
                            <strong>Atualização no ERP</strong>
                            <span>As notas fiscais informadas serão vinculadas ao projeto final selecionado.</span>
                        </Box>
                    </Box>
                </Box>

                <Box className='project-change-summary'>
                    <Box className='project-change-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da atualização</strong>
                        </Box>
                    </Box>

                    <Box className='project-change-summary-list'>
                        <Box>
                            <span>Notas fiscais</span>
                            <strong className={nfsList.length === 0 ? 'is-empty' : undefined}>
                                {nfsList.length === 0 ? 'Não informadas' : `${nfsList.length} identificada${nfsList.length === 1 ? '' : 's'}`}
                            </strong>
                        </Box>
                        <Box>
                            <span>Novo projeto</span>
                            <strong className={!selectedProjetoFinal ? 'is-empty' : undefined}>
                                {selectedProjetoFinal?.NomeProjeto || 'Não selecionado'}
                            </strong>
                        </Box>
                    </Box>

                    <Box className='project-change-attention'>
                        <SyncAltRoundedIcon />
                        <p>
                            Revise a lista de NFs e os projetos antes de confirmar. A alteração será aplicada no ERP para todos os itens informados.
                        </p>
                    </Box>

                    <Box className='project-change-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/2 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<EditRoundedIcon />}>
                        Alterar Projeto
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default AlterarProjeto;
